import api from "./api";

export interface MFASession {
  mfaToken: string;
  userId: number;
  roleId: number;
  expiresAt: Date;
  cellphone: string;
  email: string;
}

export interface MFAVerificationResult {
  success: boolean;
  token?: string;
  mfaSessionToken?: string;
  user?: any;
  error?: string;
  type?: "expiredSession";
}

class MFAService {
  private mfaSession: MFASession | null = null;

  setMFASession(session: MFASession) {
    this.mfaSession = session;
    sessionStorage.setItem("mfa_session", JSON.stringify(session));
  }

  getMFASession(): MFASession | null {
    if (this.mfaSession) return this.mfaSession;

    const stored = sessionStorage.getItem("mfa_session");
    if (!stored) return null;

    const parsed = JSON.parse(stored);
    const expiresAt = new Date(parsed.expiresAt).getTime();

    if (expiresAt < Date.now()) {
      this.clearMFASession();
      return null;
    }

    this.mfaSession = parsed;
    return this.mfaSession;
  }

  clearMFASession() {
    this.mfaSession = null;
    sessionStorage.removeItem("mfa_session");
  }

  async verifyMFACode(
    code: string,
    role_id: number,
    status?: number,
    cellphone?: string,
  ): Promise<MFAVerificationResult> {
    const session = this.getMFASession();
    if (!session) {
      return {
        success: false,
        error: "Sessão expirada, Faça login novamente",
        type: "expiredSession",
      };
    }

    try {
      const response = await api.post("/auth/validate/code", {
        mfaToken: session.mfaToken,
        code,
        userId: session.userId,
        status,
        role_id,
        cellphone,
      });

      if (response.data.token) {
        return {
          success: true,
          token: response.data.token,
          user: response.data.user,
          mfaSessionToken: response.data.mfaSessionToken,
        };
      } else {
        return { success: false, error: "Código inválido" };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data || "Erro ao verificar código",
      };
    }
  }

  async resendCode(method: "sms" | "whatsapp" | "email" = "sms") {
    const session = this.getMFASession();

    if (!session) {
      throw new Error("Sessão MFA não encontrada");
    }

    try {
      const response = await api.post("/auth/resend/sms", {
        userId: session.userId,
        method,
      });

      if (response.data.mfaSessionToken) {
        this.clearMFASession();
        this.setMFASession({
          ...session,
          mfaToken: response.data.mfaSessionToken,
          expiresAt: new Date(response.data.expiresAt),
        });
      }

      return response.data;
    } catch (error) {
      return false;
    }
  }

  isMFASessionvalid(): boolean {
    const session = this.getMFASession();

    if (!session) return false;

    const expiresAt = new Date(session.expiresAt);
    return expiresAt > new Date();
  }
}

export default new MFAService();
